import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, Loader2, Lock, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Navbar } from "@/components/Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Unlock() {
  const { code: codeParam } = useParams();
  const [code, setCode] = useState(codeParam || "");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const unlock = async (value) => {
    const c = (value || code).trim().toUpperCase();
    if (!c) {
      toast.error("Please enter your unlock code.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/messages/${encodeURIComponent(c)}`);
      setMessage(data);
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.error("No message found for that code. Please check and try again.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (codeParam) unlock(codeParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeParam]);

  return (
    <div className="min-h-screen bg-obsidian bg-grain">
      <Navbar />
      <div className="max-w-2xl mx-auto px-5 sm:px-8 pt-32 pb-20">
        {!message ? (
          <div className="text-center">
            <span className="inline-grid place-items-center w-16 h-16 rounded-full border border-gold/40 text-gold mb-6">
              <Lock className="w-7 h-7" />
            </span>
            <h1 className="font-serif text-3xl sm:text-4xl font-medium text-gold-light">Unlock a message</h1>
            <p className="text-slate-400 mt-4 max-w-md mx-auto">
              Enter the unlock code left for you to watch the message that was recorded.
            </p>

            <div className="glass-card rounded-2xl p-8 mt-10">
              <Input
                data-testid="unlock-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && unlock()}
                placeholder="MLM-XXXX-XXXX-XXXX"
                className="text-center font-mono text-2xl tracking-[0.25em] h-16 bg-obsidian/60 border-gold/20 focus-visible:ring-gold/40 text-gold-light"
              />
              <Button
                data-testid="unlock-submit-button"
                onClick={() => unlock()}
                disabled={loading}
                className="w-full mt-5 bg-gold hover:bg-gold-hover text-obsidian font-semibold rounded-full h-12"
              >
                {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Opening…</> : <><KeyRound className="w-5 h-5 mr-1" /> Unlock message</>}
              </Button>
            </div>
          </div>
        ) : (
          <div className="animate-fade-up" style={{ opacity: 0 }} data-testid="unlocked-message">
            <div className="text-center mb-8">
              <Flame className="w-6 h-6 text-gold mx-auto mb-4 animate-flicker" />
              {message.recipient_name && (
                <p className="text-sm uppercase tracking-[0.25em] text-gold/70 mb-3">For {message.recipient_name}</p>
              )}
              <h1 data-testid="message-title" className="font-serif text-3xl sm:text-4xl font-medium text-gold-light text-glow-gold">
                {message.title}
              </h1>
            </div>

            {message.note && (
              <div className="glass-card rounded-2xl p-7 mb-6">
                <p data-testid="message-note" className="font-serif italic text-lg text-slate-200 leading-relaxed text-center whitespace-pre-line">
                  "{message.note}"
                </p>
              </div>
            )}

            <div className="glass-card rounded-2xl p-4">
              <video
                data-testid="message-video"
                src={`${API}/messages/${encodeURIComponent(message.code)}/video`}
                controls
                autoPlay
                playsInline
                className="w-full rounded-xl bg-black aspect-video object-contain"
              />
            </div>

            <div className="text-center mt-8">
              <Button
                data-testid="unlock-another-button"
                onClick={() => { setMessage(null); setCode(""); }}
                variant="outline"
                className="rounded-full border-gold/30 text-slate-200 bg-transparent hover:bg-gold/10 hover:text-gold"
              >
                Unlock another message
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
